import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

const lower = (v) => String(v ?? '').trim().toLowerCase();

/**
 * Search, filter and sort for the admin card dashboards. Everything lives in
 * the URL (?q=…&state=florida&sort=updated) so Back from a detail page and
 * bookmarked links keep the view.
 *
 * config:
 *   search(item)      -> strings the search box matches (every word must match)
 *   filters[]         -> { key, label, values(item) -> string[],
 *                          keepOrder?: list options in first-seen order }
 *   sorts[]           -> { key, label, compare(a, b) }
 *   defaultSort       -> key of the sort used when the URL has none
 *
 * Options are built from the data with counts. Several options in one filter
 * match any of them; different filters must all match.
 *
 * Keep config stable (module constant or useMemo); it is a memo dependency.
 */
export default function useDashboardFilters(items, config) {
  const { search, filters = [], sorts, defaultSort } = config;
  const [params, setParams] = useSearchParams();
  const paramString = params.toString();

  const query = params.get('q') || '';
  const sortKey = sorts.some((s) => s.key === params.get('sort')) ? params.get('sort') : defaultSort;

  // { value (merge key), label (most common spelling), count }
  const optionsByFilter = useMemo(
    () =>
      filters.map((filter) => {
        const byKey = new Map();
        items.forEach((item) => {
          const seen = new Set();
          filter.values(item).forEach((raw) => {
            const label = String(raw ?? '').trim();
            const key = lower(label);
            if (!key || seen.has(key)) return;
            seen.add(key);
            const option = byKey.get(key) || { value: key, count: 0, spellings: new Map() };
            option.count += 1;
            option.spellings.set(label, (option.spellings.get(label) || 0) + 1);
            byKey.set(key, option);
          });
        });

        const options = [...byKey.values()].map(({ value, count, spellings }) => ({
          value,
          count,
          label: [...spellings.entries()].sort((a, b) => b[1] - a[1])[0][0],
        }));
        if (!filter.keepOrder) options.sort((a, b) => a.label.localeCompare(b.label));
        return options;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, filters]
  );

  const visible = useMemo(() => {
    const words = lower(query).split(/\s+/).filter(Boolean);
    const selected = filters.map((f) => params.getAll(f.key));

    const matches = items.filter((item) => {
      if (words.length) {
        const haystack = search(item).filter(Boolean).join(' ').toLowerCase();
        if (!words.every((w) => haystack.includes(w))) return false;
      }
      return filters.every((filter, i) => {
        if (selected[i].length === 0) return true;
        const keys = filter.values(item).map(lower);
        return selected[i].some((s) => keys.includes(s));
      });
    });

    const { compare } = sorts.find((s) => s.key === sortKey);
    return [...matches].sort(compare);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, paramString, sortKey, search, filters, sorts]);

  const update = (mutate) => {
    const next = new URLSearchParams(params);
    mutate(next);
    setParams(next, { replace: true });
  };

  const active = Boolean(query) || filters.some((f) => params.getAll(f.key).length > 0);

  return {
    visible,
    total: items.length,
    active,
    clear: () =>
      update((next) => {
        next.delete('q');
        filters.forEach((f) => next.delete(f.key));
      }),
    barProps: {
      query,
      onQueryChange: (value) => update((next) => (value ? next.set('q', value) : next.delete('q'))),
      filters: filters.map((filter, i) => ({
        key: filter.key,
        label: filter.label,
        options: optionsByFilter[i],
        selected: params.getAll(filter.key),
        onChange: (values) =>
          update((next) => {
            next.delete(filter.key);
            values.forEach((v) => next.append(filter.key, v));
          }),
      })),
      sorts,
      sortKey,
      onSortChange: (key) =>
        update((next) => (key === defaultSort ? next.delete('sort') : next.set('sort', key))),
    },
  };
}
